import RpcService  from '@/core/services/Rpc/RpcService';
import RpcResponse from '@/core/services/Rpc/RpcResponse';

import type PaymentRequest     from '@/domains/PaymentRequests/PaymentRequest';
import type RpcDataPagination  from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort        from '@/core/services/Rpc/RpcDataSort';
import type Partner from '@/domains/Partners/Partner';
import type Person from '@/domains/Persons/Person';
import type OrderCategory from '@/domains/Orders/OrderCategory';

/**
 * DAO для работы с заявками на платёж
 */
class PaymentRequestsDAO {

	/**
	 * Загружает одну заявку на платёж по ID
	 */
	static async get(id: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.get', { id });
	}

	/**
	 * Загружает список заявок на платёж
	 */
	static async getList(
		filters?: {
      idPattern?: string;
      receiverName?: string;
      purpose?: string;
      plannedPaymentDate?: { dateFrom: unknown; dateTo: unknown };
      actualPaymentDate?: { dateFrom: unknown; dateTo: unknown };
      isExported?: boolean;
      isPaid?: boolean;
      isCanceled?: boolean;
      isProcessing?: boolean;
      priorityType?: { id: number };
      responsible?: Person;
      partner?: Partner;
      isMine?: boolean;
      orderCategory?: OrderCategory;
      isOfferBased?: boolean;
    },
		pagination?: RpcDataPagination,
		sort?: RpcDataSort,
	): Promise<RpcResponse> {
		return await RpcService.makeRequest(
			'paymentRequests.getList',
      {
        ...filters,
        responsible: filters?.responsible ? { id: filters.responsible.id } : undefined,
        partner: filters?.partner ? { id: filters.partner.id } : undefined,
        orderCategory: filters?.orderCategory ? { id: filters.orderCategory.id } : undefined,
        priorityType: filters?.priorityType ? { id: filters.priorityType.id } : undefined,
      },
			pagination,
			sort,
			true, null, null, false,
			{ abortPrevious: true },
		);
	}

	/**
	 * Загружает список позиций заявки на платёж по позиции заказа.
	 * Бэк читает orderPosition->id, шлём тонкий { id }. Модель в DAO не тянем.
	 */
	static async getPositionList(orderPosition: { id: number }): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.getPositionList', {
			orderPosition: { id: orderPosition.id },
		});
	}

	/**
	 * Создаёт заявку на платёж
	 */
	static async add(paymentRequest: PaymentRequest): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.add', { paymentRequest });
	}

	/**
	 * Обновляет заявку на платёж
	 */
	static async update(paymentRequest: PaymentRequest): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.update', { paymentRequest });
	}

	/**
	 * Обновляет приоритет заявки
	 */
	static async updateStatus(paymentRequest: { id: number; priorityType?: { id: number } }): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.updateStatus', { paymentRequest });
	}

	/**
	 * Отменяет заявку на платёж
	 */
	static async cancel(id: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('paymentRequests.cancel', { id });
	}
}

export default PaymentRequestsDAO;
